import { z } from "zod";
import { surfaceEnvelope } from "./shared";
import { SkillSchema, DomainSchema, ConversationStatsSchema } from "./identity";
import { NoteDocSchema } from "./note-doc";
import { MeetingParticipantSchema, SpeakerStatsSchema } from "./conversations-surface";

export const TeamActivityItemSchema = z.object({ id: z.string(), conversation_title: z.string().nullish(), topic: z.string(), site: z.string(), last_captured_at: z.string(), author_name: z.string(), author_role: z.string() }).passthrough();
export const TeamTopicCountSchema = z.object({ topic: z.string(), count: z.number() }).passthrough();
export const TeamActiveMemberSchema = z.object({ display_name: z.string(), display_role: z.string(), last_active: z.string().nullish(), conversations_this_week: z.number() }).passthrough();
export const TeamStatsSchema = z.object({ total_conversations: z.number(), total_tokens: z.number(), active_member_count: z.number() }).passthrough();

export const TeamHomeSurfaceDataSchema = z.object({
  activity_feed: z.array(TeamActivityItemSchema).default([]),
  topic_counts: z.array(TeamTopicCountSchema).default([]),
  active_members: z.array(TeamActiveMemberSchema).default([]),
  stats: TeamStatsSchema,
}).passthrough();

export const TeamMemberSchema = z.object({
  user_id: z.string(), display_name: z.string(), display_role: z.string(), role: z.string(),
  joined_at: z.string(), visibility_tier: z.string(),
  recent_topics: z.array(z.string()).default([]), tools_used: z.array(z.string()).default([]),
  last_active: z.string().nullish(),
  skills: z.array(SkillSchema).nullish(), domains: z.array(DomainSchema).nullish(),
  current_focus: z.array(z.string()).nullish(), conversation_stats: ConversationStatsSchema.nullish(),
}).passthrough();

export const TeamMembersSurfaceDataSchema = z.object({ members: z.array(TeamMemberSchema).default([]) }).passthrough();

export const TeamConversationListItemSchema = z.object({
  id: z.string(), conversation_title: z.string().nullish(), topic: z.string(), summary: z.string(),
  site: z.string(), estimated_tokens: z.number(), turn_count: z.number(), last_captured_at: z.string(),
  author_name: z.string(), author_role: z.string(), project_name: z.string().nullish(),
}).passthrough();

export const TeamConversationsSurfaceDataSchema = z.object({
  conversations: z.array(TeamConversationListItemSchema).default([]),
  total_count: z.number(),
}).passthrough();

export const TeamConversationDetailDataSchema = z.object({
  id: z.string(), conversation_title: z.string().nullish(), topic: z.string(), summary: z.string(),
  // The team route falls back to `null` (not "") for compacted_text — nullish, matching the route.
  site: z.string(), compacted_text: z.string().nullish(), estimated_tokens: z.number(), turn_count: z.number(),
  entity_tags: z.array(z.string()).default([]), first_captured_at: z.string(), last_captured_at: z.string(),
  author_name: z.string(), author_role: z.string(), project_name: z.string().nullish(),
  // Returned by the team route since PR1, declared here in PR3 to close the contract drift.
  team_project_id: z.string().nullish(),
  team_project_name: z.string().nullish(),
  meeting_participants: z.array(MeetingParticipantSchema).nullish(),
  speaker_stats: SpeakerStatsSchema.nullish(),
  // Conversation note redesign PR1 (Task 10): the assembled NoteDoc (viewer + query-engine
  // canonical read), the briefing state driving the detail state badge, and the
  // entry_kind discriminator. `.nullish()` — absent on legacy rows / not yet built by every writer.
  note_doc: NoteDocSchema.nullish(),
  briefing_category: z.string().nullish(),
  entry_kind: z.string().nullish(),
  // PR-3 "Open in source" (brief-phase1 spec §2): server-derived source-first deep link. Null for
  // ai-chat/claude-code notes (owner-only platform links), for a teammate's email note (per-mailbox
  // thread id), and whenever a viewer-scoped mirror join finds nothing — the note page itself
  // is the designed fallback.
  source_url: z.string().nullish(),
}).passthrough();

export const TeamMapNodeSchema = z.object({ id: z.string(), title: z.string().nullish(), topic: z.string(), site: z.string(), project_name: z.string().nullish(), last_captured_at: z.string(), author_name: z.string(), author_role: z.string() }).passthrough();
export const TeamMapEdgeSchema = z.object({ source: z.string(), target: z.string(), similarity: z.number() }).passthrough();
export const TeamMapSurfaceDataSchema = z.object({ nodes: z.array(TeamMapNodeSchema).default([]), edges: z.array(TeamMapEdgeSchema).default([]) }).passthrough();

export const AnalyticsProjectSchema = z.object({ project_name: z.string(), estimated_tokens: z.number(), conversation_count: z.number(), pct: z.number() }).passthrough();
export const AnalyticsSiteSchema = z.object({ site: z.string(), estimated_tokens: z.number(), pct: z.number() }).passthrough();
export const AnalyticsWeekSchema = z.object({ week_start: z.string(), estimated_tokens: z.number(), conversation_count: z.number() }).passthrough();

export const TeamAnalyticsSurfaceDataSchema = z.object({
  period_start: z.string(), period_end: z.string(),
  total_tokens: z.number(), total_conversations: z.number(), active_members: z.number(),
  by_project: z.array(AnalyticsProjectSchema).default([]),
  by_site: z.array(AnalyticsSiteSchema).default([]),
  weekly_trend: z.array(AnalyticsWeekSchema).default([]),
}).passthrough();

// D6 team-project flag read-exposure: GET /api/surfaces/team/projects. RESPONSE schema —
// booleans default false; conversation_count defaults 0 (mirrors MVP lib/dashboard/contracts).
export const TeamProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  pinned: z.boolean().default(false),
  hidden: z.boolean().default(false),
  user_named: z.boolean().default(false),
  conversation_count: z.number().default(0),
  // Distinct teammates with a viewer-visible conversation in the project. nullish (not default 0) so a
  // dashboard built against an older backend that omits it falls back to its derived count rather than
  // flashing "0 people" — bug-353.
  person_count: z.number().nullish(),
  // Most recent non-private conversation in the project (ISO). null = no clustered activity yet.
  // Drives recency sort + the active/archived split (archived = hidden OR inactive >30d OR null).
  last_activity_at: z.string().nullish(),
}).passthrough();
// unclustered_count = non-private team conversations with no project (the "Unclustered" bucket size).
export const TeamProjectsResponseSchema = z.object({
  projects: z.array(TeamProjectSchema).default([]),
  unclustered_count: z.number().default(0),
}).passthrough();

export const TeamHomeSurfaceResponseSchema = surfaceEnvelope(TeamHomeSurfaceDataSchema);
export const TeamMembersSurfaceResponseSchema = surfaceEnvelope(TeamMembersSurfaceDataSchema);
export const TeamConversationsSurfaceResponseSchema = surfaceEnvelope(TeamConversationsSurfaceDataSchema);
export const TeamConversationDetailResponseSchema = surfaceEnvelope(TeamConversationDetailDataSchema);
export const TeamMapSurfaceResponseSchema = surfaceEnvelope(TeamMapSurfaceDataSchema);
export const TeamAnalyticsSurfaceResponseSchema = surfaceEnvelope(TeamAnalyticsSurfaceDataSchema);

export type TeamActivityItem = z.infer<typeof TeamActivityItemSchema>;
export type TeamTopicCount = z.infer<typeof TeamTopicCountSchema>;
export type TeamActiveMember = z.infer<typeof TeamActiveMemberSchema>;
export type TeamStats = z.infer<typeof TeamStatsSchema>;
export type TeamHomeSurfaceData = z.infer<typeof TeamHomeSurfaceDataSchema>;
export type TeamMember = z.infer<typeof TeamMemberSchema>;
export type TeamMembersSurfaceData = z.infer<typeof TeamMembersSurfaceDataSchema>;
export type TeamConversationListItem = z.infer<typeof TeamConversationListItemSchema>;
export type TeamConversationsSurfaceData = z.infer<typeof TeamConversationsSurfaceDataSchema>;
export type TeamConversationDetailData = z.infer<typeof TeamConversationDetailDataSchema>;
export type TeamMapNode = z.infer<typeof TeamMapNodeSchema>;
export type TeamMapEdge = z.infer<typeof TeamMapEdgeSchema>;
export type TeamMapSurfaceData = z.infer<typeof TeamMapSurfaceDataSchema>;
export type TeamProject = z.infer<typeof TeamProjectSchema>;
export type TeamProjectsResponse = z.infer<typeof TeamProjectsResponseSchema>;
export type AnalyticsProject = z.infer<typeof AnalyticsProjectSchema>;
export type AnalyticsSite = z.infer<typeof AnalyticsSiteSchema>;
export type AnalyticsWeek = z.infer<typeof AnalyticsWeekSchema>;
export type TeamAnalyticsSurfaceData = z.infer<typeof TeamAnalyticsSurfaceDataSchema>;
export type TeamHomeSurfaceResponse = z.infer<typeof TeamHomeSurfaceResponseSchema>;
export type TeamMembersSurfaceResponse = z.infer<typeof TeamMembersSurfaceResponseSchema>;
export type TeamConversationsSurfaceResponse = z.infer<typeof TeamConversationsSurfaceResponseSchema>;
export type TeamConversationDetailResponse = z.infer<typeof TeamConversationDetailResponseSchema>;
export type TeamMapSurfaceResponse = z.infer<typeof TeamMapSurfaceResponseSchema>;
export type TeamAnalyticsSurfaceResponse = z.infer<typeof TeamAnalyticsSurfaceResponseSchema>;
